import React, { useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import Select from "react-select";
import { DateRangePicker } from "rsuite";
import { useNarrowWindow } from "../common/useNarrowWindow";
import dayjs from "dayjs";
import Header from "../Header/Header";
import { styles } from "../MainPage/MainPage";
import { messages } from "../common/notification";
import { useStoreListRefresh } from "../common/useStoreList";
import { useDashboardStoreDefault } from "../common/useDashboardStore";
import { fetchRegions } from "../StoreRegions/api";
import { fetchStoreAnalytics } from "../Reports/api";
import {
  MAX_RANGE_DAYS,
  REPORT_TYPES,
  STORE_ANALYTICS_CONCURRENCY,
} from "../Reports/constants";
import {
  STORE_ANALYTICS_COLUMNS,
  STORE_METRICS,
  downloadCsv,
  mapWithConcurrency,
  reportFileName,
  storeAnalyticsRows,
} from "../Reports/functions";
import { MultiSelect, selectStyles, storeOptionLabel } from "../Reports/selects";
import FilterHeaderCell, { ActiveFilters } from "../common/FilterHeaderCell";
import { filterOptions, matchesColumnFilters } from "../common/columnFilters";
import { pageOf, regionOptions, storesInRegions } from "./functions";
import "../Reports/Reports.css";
import "./StoreAnalytics.css";

const { afterToday, allowedMaxDays, combine } = DateRangePicker;

// Same limit as the Store Analytics report: each store is one heavy query.
const MAX_DAYS = MAX_RANGE_DAYS[REPORT_TYPES.storeAnalytics];
const PAGE_SIZE_OPTIONS = [25, 50, 100, 250, 500].map((size) => ({ value: size, label: `${size} rows` }));
const DEFAULT_PAGE_SIZE = 100;

const METRIC_OPTIONS = STORE_METRICS.map(([value, label]) => ({ value, label }));

const plural = (count, word, many = `${word}s`) => `${count} ${count === 1 ? word : many}`;

function StoreAnalytics() {
  // The two-month date calendar is about 650px wide; below this it shows one.
  const narrowWindow = useNarrowWindow(1280);
  const housesList = useSelector((state) => state.housesList);
  const { refreshing: storesRefreshing, refresh: refreshStoreList } = useStoreListRefresh();

  const [regions, setRegions] = useState([]);
  const [selectedRegions, setSelectedRegions] = useState([]);
  const [selectedStores, setSelectedStores] = useState([]);
  const [dateRange, setDateRange] = useState({ startDate: null, endDate: null });
  const [selectedMetrics, setSelectedMetrics] = useState(METRIC_OPTIONS);

  // { done, total } while stores are being read.
  const [progress, setProgress] = useState(null);
  // What the last run read: [{ property, analytics }] per store, and its range.
  const [result, setResult] = useState(null);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [preparing, setPreparing] = useState(false);

  // Bumped on every run, so a slower earlier run cannot overwrite a newer one.
  const runId = useRef(0);

  // /v1/regions fills the region list; regions the stores carry are added to
  // it, so the filter still works if the call fails or is not allowed.
  useEffect(() => {
    fetchRegions().then((response) => {
      if (response.success) setRegions(response.data);
    });
  }, []);

  const stores = useMemo(
    () =>
      [...(housesList ?? [])]
        .filter((store) => store.code)
        .sort((a, b) => (a.houseName || "").localeCompare(b.houseName || "")),
    [housesList]
  );

  const regionOptionList = useMemo(() => regionOptions(stores, regions), [stores, regions]);

  const storeOptions = useMemo(
    () =>
      storesInRegions(
        stores,
        selectedRegions.map((option) => option.value)
      ).map((store) => ({
        value: store.code,
        label: store.houseName || store.code,
        property: { houseId: store.code, houseName: store.houseName || store.code },
      })),
    [stores, selectedRegions]
  );

  // Opens on the store picked on the Dashboard.
  useDashboardStoreDefault("storeAnalytics", storeOptions, selectedStores, setSelectedStores);

  // Stores outside the picked regions, or gone from the list, are dropped.
  useEffect(() => {
    const available = new Set(storeOptions.map((option) => option.value));
    setSelectedStores((picked) => {
      const kept = picked.filter((option) => available.has(option.value));
      return kept.length === picked.length ? picked : kept;
    });
  }, [storeOptions]);

  const metricKeys = useMemo(
    () => selectedMetrics.map((option) => option.value),
    [selectedMetrics]
  );

  // Metrics only pick columns, so changing them reshapes what was read
  // without fetching again.
  const rows = useMemo(() => {
    if (!result) return [];
    const all = [];
    for (const { property, analytics } of result.stores) {
      for (const row of storeAnalyticsRows(analytics, property, metricKeys)) all.push(row);
    }
    return all;
  }, [result, metricKeys]);

  const columns = useMemo(
    () => [
      ...STORE_ANALYTICS_COLUMNS,
      ...STORE_METRICS.filter(([metric]) => metricKeys.includes(metric)).map(([, label]) => label),
    ],
    [metricKeys]
  );

  // Header filters on the fixed columns, each picked from what the rows hold;
  // the metric columns are numbers and are not filtered. Date and Time keep the
  // table's order (days, then the day's slots) rather than sorting as text.
  const [columnFilters, setColumnFilters] = useState({});
  const setColumnFilter = (field, value) =>
    setColumnFilters((current) => ({ ...current, [field]: value }));
  const filterSpecs = useMemo(
    () => STORE_ANALYTICS_COLUMNS.map((field) => ({ field, kind: "select" })),
    []
  );
  const columnFilterOptions = useMemo(
    () =>
      Object.fromEntries(
        STORE_ANALYTICS_COLUMNS.map((field) => [
          field,
          filterOptions(rows, field, { sorted: field !== "Date" && field !== "Time" }),
        ])
      ),
    [rows]
  );
  const filteredRows = useMemo(
    () => rows.filter((row) => matchesColumnFilters(row, columnFilters, filterSpecs)),
    [rows, columnFilters, filterSpecs]
  );
  // A new Show starts unfiltered: the old picks may not exist in the new rows.
  useEffect(() => setColumnFilters({}), [result]);

  useEffect(() => setPage(1), [result, metricKeys, columnFilters]);

  const loading = progress !== null;

  const show = async () => {
    if (selectedStores.length === 0) {
      messages.error("Select a store");
      return;
    }
    if (!dateRange.startDate || !dateRange.endDate) {
      messages.error("Select a date range");
      return;
    }
    if (selectedMetrics.length === 0) {
      messages.error("Select a metric");
      return;
    }

    const id = ++runId.current;
    const properties = selectedStores.map((option) => option.property);
    const range = { ...dateRange };
    const startDate = dayjs(range.startDate).format("YYYY-MM-DD");
    const endDate = dayjs(range.endDate).format("YYYY-MM-DD");

    setProgress({ done: 0, total: properties.length });

    const read = await mapWithConcurrency(
      properties,
      STORE_ANALYTICS_CONCURRENCY,
      async (property) => {
        const analytics = await fetchStoreAnalytics(property.houseId, startDate, endDate);
        if (id === runId.current) {
          setProgress((current) => current && { ...current, done: current.done + 1 });
        }
        return { property, analytics };
      }
    );

    if (id !== runId.current) return;
    setResult({ stores: read, dateRange: range });
    setProgress(null);
  };

  const download = () => {
    setPreparing(true);
    // Deferred a tick so "Preparing..." is on screen while a large file is
    // put together.
    setTimeout(() => {
      try {
        // What the table shows: the header filters apply to the file too.
        downloadCsv(
          filteredRows,
          reportFileName({ date: result.dateRange, type: REPORT_TYPES.storeAnalytics })
        );
      } catch (error) {
        console.error("CSV download failed", error);
        messages.error("Could not build the file");
      } finally {
        setPreparing(false);
      }
    }, 0);
  };

  const shown = pageOf(filteredRows, page, pageSize);

  const storesWithData = result
    ? result.stores.filter(({ analytics }) => analytics?.telemetry?.length > 0).length
    : 0;
  const deviceCount = result
    ? new Set(
        result.stores.flatMap(({ analytics }) =>
          (analytics?.telemetry ?? []).map((entry) => entry?.deviceId).filter(Boolean)
        )
      ).size
    : 0;

  return (
    <div style={{ ...styles.container }}>
      <div style={{ ...styles.rightContainer }}>
        <Header />

        <div className="rp-page">
          <div className="rp-title">Store Analytics</div>
          <div className="rp-subtitle">
            {selectedStores.length === 0
              ? "Pick a region and stores to see their 15 minute analytics"
              : `${plural(selectedStores.length, "store")} selected`}
          </div>

          <div className="rp-filters">
            <div className="rp-field">
              <span className="rp-label">Region</span>
              <MultiSelect
                options={regionOptionList}
                value={selectedRegions}
                onChange={setSelectedRegions}
                summary={{ empty: "All regions", nouns: "regions" }}
                noOptionsMessage={() => "No regions"}
                width="200px"
              />
            </div>

            <div className="rp-field">
              <span className="rp-label">Stores</span>
              <MultiSelect
                options={storeOptions}
                value={selectedStores}
                onChange={setSelectedStores}
                summary={{ empty: "Select stores...", nouns: "stores" }}
                formatOptionLabel={storeOptionLabel}
                onMenuOpen={refreshStoreList}
                isLoading={storesRefreshing}
                noOptionsMessage={() =>
                  selectedRegions.length ? "No stores in these regions" : "No stores"
                }
                width="260px"
              />
            </div>

            <div className="rp-field">
              <span className="rp-label">Date Range (max {MAX_DAYS} days)</span>
              <DateRangePicker
                shouldDisableDate={combine(afterToday(), allowedMaxDays(MAX_DAYS))}
                ranges={[]}
                // Lined up with the field's right edge and flipped above it when there
                // is no room below, so the calendar stays on screen; one month instead of
                // two on narrow windows.
                placement="autoVerticalEnd"
                showOneCalendar={narrowWindow}
                placeholder={`Up to ${MAX_DAYS} days`}
                format="dd-MM-yyyy"
                character=" - "
                value={
                  dateRange.startDate && dateRange.endDate
                    ? [dateRange.startDate, dateRange.endDate]
                    : null
                }
                onChange={(range) =>
                  setDateRange(
                    range
                      ? { startDate: range[0], endDate: range[1] }
                      : { startDate: null, endDate: null }
                  )
                }
                style={{ width: 240 }}
              />
            </div>

            <div className="rp-field">
              <span className="rp-label">Metrics</span>
              <MultiSelect
                options={METRIC_OPTIONS}
                value={selectedMetrics}
                onChange={setSelectedMetrics}
                summary={{ empty: "Select metrics...", nouns: "metrics" }}
                isSearchable={false}
                width="200px"
              />
            </div>

            <button type="button" className="rp-get" onClick={show} disabled={loading}>
              {loading ? "Loading..." : "Show"}
            </button>
          </div>

          {(loading || result) && (
            <div className="sa-toolbar">
              <div className="sa-summary">
                {loading
                  ? `Reading stores... ${progress.done} of ${progress.total}`
                  : `${plural(storesWithData, "store")} with data of ${result.stores.length} · ${plural(
                      deviceCount,
                      "device"
                    )} · ${plural(rows.length, "row")}`}
              </div>
              {result && !loading && filteredRows.length > 0 && (
                <button
                  type="button"
                  className="rp-action rp-action-primary"
                  onClick={download}
                  disabled={preparing}
                >
                  {preparing ? "Preparing..." : "Download CSV"}
                </button>
              )}
            </div>
          )}

          <ActiveFilters
            specs={filterSpecs}
            labels={Object.fromEntries(STORE_ANALYTICS_COLUMNS.map((column) => [column, column]))}
            filters={columnFilters}
            options={columnFilterOptions}
            onChange={setColumnFilter}
            onClear={() => setColumnFilters({})}
          />

          <div className="rp-table-wrap rp-table-scroll sa-table-wrap">
            <table className="rp-table">
              <thead>
                <tr>
                  {columns.map((column) =>
                    STORE_ANALYTICS_COLUMNS.includes(column) ? (
                      <FilterHeaderCell
                        key={column}
                        label={column}
                        kind="select"
                        value={columnFilters[column]}
                        options={columnFilterOptions[column]}
                        onChange={(value) => setColumnFilter(column, value)}
                      />
                    ) : (
                      <th key={column}>{column}</th>
                    )
                  )}
                </tr>
              </thead>
              <tbody>
                {shown.rows.length === 0 ? (
                  <tr>
                    <td className="rp-empty" colSpan={columns.length}>
                      {loading
                        ? "Loading analytics..."
                        : rows.length > 0
                          ? "No rows match these filters."
                          : result
                          ? "No analytics for the selected stores and dates."
                          : "Pick stores and a date range, then press Show."}
                    </td>
                  </tr>
                ) : (
                  shown.rows.map((row, index) => (
                    <tr key={shown.start + index}>
                      {columns.map((column) => (
                        <td key={column}>{row[column] === "" ? "-" : row[column]}</td>
                      ))}
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {filteredRows.length > 0 && (
            <div className="sa-pager">
              <span>Rows per page</span>
              <Select
                options={PAGE_SIZE_OPTIONS}
                value={PAGE_SIZE_OPTIONS.find((option) => option.value === pageSize)}
                onChange={(option) => {
                  // Stay on the rows being looked at rather than jumping back
                  // to the start.
                  setPage(Math.floor(shown.start / option.value) + 1);
                  setPageSize(option.value);
                }}
                isSearchable={false}
                menuPlacement="auto"
                styles={selectStyles("120px")}
              />
              <span>
                Rows {shown.start + 1}-{shown.start + shown.rows.length} of {filteredRows.length}
                {filteredRows.length !== rows.length && ` (filtered from ${rows.length})`}
              </span>
              <button
                type="button"
                className="rp-action"
                onClick={() => setPage(shown.page - 1)}
                disabled={shown.page === 1}
              >
                Previous
              </button>
              <span>
                Page {shown.page} of {shown.pages}
              </span>
              <button
                type="button"
                className="rp-action"
                onClick={() => setPage(shown.page + 1)}
                disabled={shown.page === shown.pages}
              >
                Next
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default StoreAnalytics;
